import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Toggle } from 'rsuite';
import { Draggable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEquals } from '@fortawesome/free-solid-svg-icons';
import { Link, useParams } from 'react-router-dom';
import { WhisperComponent } from '../../developers/page-components/WhisperComponent';
import { numberFormatter, numberFormatterCount, valueFormatter } from '../../../utils/utilities';
import { ProviderTableMultiSelector } from '../../provider/ProviderTableMultiSelector';
import { PaymentGatewayCheckbox } from './PaymentGatewayCheckbox';
import { usePaymentContext } from '../../../store/payments/payments-context';
import { fetchPaymentGatewayData, setPaymentGateway } from '../../../store/payments/actions/providers';

interface IPaymentGatewayCapabilities {
	cardBrands: string,
	cardTypes: string,
	masterCard_MonthlyMaxTransactionsCount: number,
	masterCard_MonthlyMaxValue: number,
	supportCIT: boolean,
	supportMIT: boolean,
}

export interface IPaymentGatewayItemProps {
	isDefault: boolean,
	id: string,
	isActive: boolean,
	isSandbox: boolean,
	merchantId: string,
	name: string,
	order: number,
	mcc: number | null,
	processorId: string,
	providerId: string,
	capabilities: IPaymentGatewayCapabilities;
}

export enum PAYMENT_GATEWAY_CHECKBOX_NAMES {
	SANDBOX= 'isSandbox',
	MIT_SUPPORT= 'supportMIT',
	CIT_SUPPORT= 'supportCIT',
}

export interface IPaymentGatewayTableRowProps extends IPaymentGatewayItemProps {
	index: number;
}

export const PaymentGatewayTableRow = (props: IPaymentGatewayTableRowProps): JSX.Element => {
	const { index, ...itemProps } = props;
	const { id, providerId, order, name, isSandbox, isDefault, isActive, mcc, processorId, capabilities } = itemProps;
	const { masterCard_MonthlyMaxTransactionsCount, masterCard_MonthlyMaxValue, supportCIT, supportMIT, cardBrands } = capabilities;

	const { id: merchantId } = useParams<{ id: string }>();
	const { paymentContextState: { paymentGatewayData, providerLists }, dispatch } = usePaymentContext();

	const [loading, setLoading] = useState<boolean>(false);
	const [localIsActive, setLocalIsActive] = useState<boolean>(false);
	const [localIsDefault, setLocalIsDefault] = useState<boolean>(false);
	console.log('isActive', isActive);

	useEffect(() => {
		setLocalIsActive(isActive);
		setLocalIsDefault(isDefault);
	}, [paymentGatewayData]);

	const handleInputChange = async (value): Promise<void> => {
		try {
			const defaultProviderData = paymentGatewayData?.find(el => el?.isDefault);
			setLoading(true);
			const params = { ...itemProps };
			switch (value) {
				case 'isActive':
					setLocalIsActive(!localIsActive);
					params.isActive = !localIsActive;
					break;
				case 'isDefault':
					if (defaultProviderData?.id !== id) {
						const defaultParams = { ...defaultProviderData };
						defaultParams.isDefault = false;
						await setPaymentGateway(defaultParams);
					}
					setLocalIsDefault(!localIsDefault);
					params.isDefault = !localIsDefault;
					break;
			}
			await setPaymentGateway(params);
			await fetchPaymentGatewayData({ dispatch, mid: merchantId });
			setLoading(false);
		} catch (e) {
			console.log(e);
			setLocalIsActive(localIsActive);
			setLocalIsDefault(localIsDefault);
			setLoading(false);
		}
	};

	return (
		<Draggable draggableId={id} index={index}>
			{(provided): JSX.Element => (
				<tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
					<td>
						<FontAwesomeIcon icon={faEquals} className="text-muted d-block mx-auto"/>
					</td>
					<td>{order}</td>
					<td>
						<Link
							to={`/admin/providers/processing?isActive=${localIsActive}&providerId=${providerId}`}
							target="_blank"
							className="text-muted fw-bolder cursor-pointer main-font-family fc-text-overflow"
						>
							View
						</Link>
					</td>
					<td>{name}</td>
					<td>
						{mcc ? mcc : '-'}
					</td>
					<td>{processorId ? processorId : '-'}</td>
					<td>
						<div>
							{masterCard_MonthlyMaxTransactionsCount
								? (
									<WhisperComponent
										isTooltip
										whisperText={numberFormatterCount(masterCard_MonthlyMaxTransactionsCount)}
										mainText={valueFormatter(masterCard_MonthlyMaxTransactionsCount)}
									/>
								)
								: '-'
							}
						</div>
					</td>
					<td>
						<div>
							{masterCard_MonthlyMaxValue
								? (
									<WhisperComponent
										isTooltip
										whisperText={`$${numberFormatter(masterCard_MonthlyMaxValue / 100)}`}
										mainText={`$${valueFormatter(masterCard_MonthlyMaxValue / 100)}`}
									/>
								)
								: '-'
							}
						</div>
					</td>
					<td>
						<ProviderTableMultiSelector
							value={cardBrands}
							id={id}
							itemProps={itemProps}
							fieldName='cardBrands'
							title='Card Brands'
							list={providerLists?.CardBrand}
						/>
					</td>
					<td>
						<PaymentGatewayCheckbox
							{ ...itemProps }
							value={supportMIT}
							fieldName={PAYMENT_GATEWAY_CHECKBOX_NAMES.MIT_SUPPORT}
						/>
					</td>
					<td>
						<PaymentGatewayCheckbox
							{ ...itemProps }
							value={supportCIT}
							fieldName={PAYMENT_GATEWAY_CHECKBOX_NAMES.CIT_SUPPORT}
						/>
					</td>
					<td>
						<PaymentGatewayCheckbox
							{ ...itemProps }
							value={isSandbox}
							fieldName={PAYMENT_GATEWAY_CHECKBOX_NAMES.SANDBOX}
						/>
					</td>
					<td>
						<Toggle
							size="md"
							checked={localIsActive}
							className={clsx(localIsActive ? 'is-active' : '')}
							onChange={(): Promise<void> => handleInputChange('isActive')}
							disabled={loading}
						/>
					</td>
					<td>
						<Toggle
							size="md"
							checked={localIsDefault}
							className={clsx(localIsDefault ? 'is-active' : '')}
							onChange={(): Promise<void> => handleInputChange('isDefault')}
							disabled={loading}
						/>
					</td>
				</tr>
			)}
		</Draggable>
	);
};
